import { ColumnType, SortState, TableColumn } from './table-column.model';

const daCollator = new Intl.Collator('da', { numeric: true, sensitivity: 'base' });

/**
 * Tri-state sort cycle: fremmed kolonne → asc → desc → tilbage til default-sorteringen.
 * Returns the new sort override; `undefined` means "revert to the table's default sort".
 * When the default sort IS the clicked column descending, reverting would be a no-op
 * loop, so the cycle flips to ascending instead.
 */
export function nextSort(
  clickedKey: string,
  effective: SortState | null,
  defaultSort: SortState | null,
): SortState | undefined {
  if (!effective || effective.key !== clickedKey) {
    return { key: clickedKey, direction: 'asc' };
  }
  if (effective.direction === 'asc') {
    return { key: clickedKey, direction: 'desc' };
  }
  if (defaultSort && defaultSort.key === clickedKey && defaultSort.direction === 'desc') {
    return { key: clickedKey, direction: 'asc' };
  }
  return undefined;
}

export function filterRows<T>(
  rows: readonly T[],
  searchText: string,
  searchFields?: readonly (keyof T & string)[],
): readonly T[] {
  const search = searchText.trim().toLowerCase();
  if (!search || rows.length === 0) {
    return rows;
  }
  if (searchFields && searchFields.length > 0) {
    return rows.filter(row =>
      searchFields.some(field => {
        const value = (row as Record<string, unknown>)[field];
        return value !== null && value !== undefined && String(value).toLowerCase().includes(search);
      }),
    );
  }
  return rows.filter(row =>
    Object.values(row as Record<string, unknown>).some(
      value => typeof value === 'string' && value.toLowerCase().includes(search),
    ),
  );
}

export function sortRows<T>(
  rows: readonly T[],
  sort: SortState | null,
  columns: readonly TableColumn<T>[],
): readonly T[] {
  if (!sort) {
    return rows;
  }
  const column = columns.find(c => c.key === sort.key);
  if (!column) {
    return rows;
  }
  const dir = sort.direction === 'asc' ? 1 : -1;
  const sorted = [...rows];

  if (column.sortFn) {
    sorted.sort((a, b) => column.sortFn!(a, b) * dir);
    return sorted;
  }

  const accessor = column.value ?? ((row: T) => (row as Record<string, unknown>)[column.key]);
  const type = column.type ?? inferType(rows, accessor);
  sorted.sort((a, b) => {
    const va = accessor(a);
    const vb = accessor(b);
    const aEmpty = va === null || va === undefined || va === '';
    const bEmpty = vb === null || vb === undefined || vb === '';
    if (aEmpty && bEmpty) return 0;
    // Tomme værdier ligger altid sidst, uanset retning.
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    return compareValues(va, vb, type) * dir;
  });
  return sorted;
}

function inferType<T>(rows: readonly T[], accessor: (row: T) => unknown): ColumnType {
  for (const row of rows) {
    const value = accessor(row);
    if (value === null || value === undefined || value === '') continue;
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    if (value instanceof Date) return 'date';
    return 'text';
  }
  return 'text';
}

function compareValues(a: unknown, b: unknown, type: ColumnType): number {
  switch (type) {
    case 'number':
      return Number(a) - Number(b);
    case 'boolean':
      return Number(a) - Number(b);
    case 'date':
      return toTime(a) - toTime(b);
    default:
      return daCollator.compare(String(a), String(b));
  }
}

function toTime(value: unknown): number {
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? 0 : parsed;
}
